// Schema.org Structured Data Generators for SEO Enhancement
import { useEffect } from 'react';
import { SEO_CONFIG, fixEncoding } from './helpers';

/**
 * Custom hook to inject structured data into the document head
 * This ensures the JSON-LD script is properly added to the DOM
 */
export const useStructuredData = (data, id = 'structured-data') => {
  useEffect(() => {
    if (!data) return;

    // Remove existing script with same id
    const existingScript = document.getElementById(id);
    if (existingScript) {
      existingScript.remove();
    }

    // Create new script element
    const script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.getElementById(id);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [data, id]);
};

/**
 * Organization Schema - Site-wide identity
 */
export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": SEO_CONFIG.siteName,
  "url": SEO_CONFIG.siteUrl,
  "logo": "https://customer-assets.emergentagent.com/job_shop-navigator-13/artifacts/hmk599ai_marques-and-co_logo.jpg",
  "description": SEO_CONFIG.defaultDescription,
  "sameAs": [],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "availableLanguage": ["French"]
  }
});

/**
 * WebSite Schema with SearchAction for sitelinks search box
 */
export const generateWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": SEO_CONFIG.siteName,
  "url": SEO_CONFIG.siteUrl,
  "description": SEO_CONFIG.defaultDescription,
  "inLanguage": "fr-FR",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SEO_CONFIG.siteUrl}/?query={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
});

/**
 * ItemList Schema for brand collections
 */
export const generateBrandListSchema = (brands) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Marques populaires",
  "description": "Les marques les plus recherchées sur Marques & Co",
  "numberOfItems": brands.length,
  "itemListElement": brands.slice(0, 30).map((brand, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "Brand",
      "name": typeof brand === 'string' ? brand : brand.brand,
      "url": `${SEO_CONFIG.siteUrl}/brand/${encodeURIComponent(typeof brand === 'string' ? brand : brand.brand)}`
    }
  }))
});

/**
 * CollectionPage Schema for homepage
 */
export const generateCollectionPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": SEO_CONFIG.defaultTitle,
  "description": SEO_CONFIG.defaultDescription,
  "url": SEO_CONFIG.siteUrl,
  "inLanguage": "fr-FR",
  "isPartOf": {
    "@type": "WebSite",
    "name": SEO_CONFIG.siteName,
    "url": SEO_CONFIG.siteUrl
  }
});

/**
 * Brand Schema with detailed information
 */
export const generateBrandSchema = (brandData) => {
  if (!brandData) return null;
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Brand",
    "name": brandData.brand,
    "url": `${SEO_CONFIG.siteUrl}/brand/${encodeURIComponent(brandData.brand)}`
  };

  if (brandData.logo_url) {
    schema.logo = brandData.logo_url;
    schema.image = brandData.logo_url;
  }

  if (brandData.summary) {
    // Strip HTML tags for description
    schema.description = brandData.summary.replace(/<[^>]*>/g, '').substring(0, 300);
  }

  return schema;
};

/**
 * BreadcrumbList Schema for navigation
 */
export const generateBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

/**
 * Product Schema with full offer details
 */
export const generateProductSchema = (product, query = '') => {
  if (!product) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": fixEncoding(product.title),
    "description": fixEncoding(product.description || product.title),
    "url": product.goUrl
  };

  // Add image if available
  if (product.images && product.images.length > 0) {
    schema.image = product.images.map(img => img.url);
  }

  // Add brand if available
  if (product.brand) {
    schema.brand = {
      "@type": "Brand",
      "name": product.brand
    };
  }

  // Add EAN/GTIN if available
  if (product.code?.ean) {
    schema.gtin13 = product.code.ean;
  }

  // Add offer details
  schema.offers = {
    "@type": "Offer",
    "price": product.price,
    "priceCurrency": "EUR",
    "availability": "https://schema.org/InStock",
    "url": product.goUrl,
    "priceValidUntil": new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  };

  // Add seller/merchant if available
  if (product.merchant?.name) {
    schema.offers.seller = {
      "@type": "Organization",
      "name": fixEncoding(product.merchant.name)
    };
  }

  // Add shipping info if available
  if (product.deliveryCost !== undefined) {
    schema.offers.shippingDetails = {
      "@type": "OfferShippingDetails",
      "shippingRate": {
        "@type": "MonetaryAmount",
        "value": product.deliveryCost,
        "currency": "EUR"
      },
      "shippingDestination": {
        "@type": "DefinedRegion",
        "addressCountry": "FR"
      }
    };
  }

  return schema;
};

/**
 * ItemList Schema for search results
 */
export const generateSearchResultsSchema = (products, query, totalResults) => {
  if (!products || products.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `Résultats de recherche pour "${query}"`,
    "description": `${totalResults} produits trouvés pour "${query}"`,
    "numberOfItems": totalResults,
    "itemListElement": products.slice(0, 20).map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": fixEncoding(product.title),
        "image": product.images?.[0]?.url,
        "url": product.goUrl,
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock"
        },
        ...(product.merchant?.name && {
          "seller": {
            "@type": "Organization",
            "name": fixEncoding(product.merchant.name)
          }
        }),
        ...(product.code?.ean && { "gtin13": product.code.ean })
      }
    }))
  };
};

/**
 * AggregateOffer Schema for comparison pages
 */
export const generateComparisonSchema = (productName, ean, offers, images = []) => {
  if (!productName || !offers || offers.length === 0) return null;

  const prices = offers.map(o => o.price).filter(p => p > 0);
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": productName,
    "description": `Comparez les prix de ${productName} chez ${offers.length} marchands`,
    "url": `${SEO_CONFIG.siteUrl}/compare/${ean}`
  };

  if (ean) {
    schema.gtin13 = ean;
  }

  if (images.length > 0) {
    schema.image = images;
  }

  schema.offers = {
    "@type": "AggregateOffer",
    "lowPrice": Math.min(...prices),
    "highPrice": Math.max(...prices),
    "priceCurrency": "EUR",
    "offerCount": offers.length,
    "offers": offers.slice(0, 10).map(offer => ({
      "@type": "Offer",
      "price": offer.price,
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "url": offer.url,
      "seller": {
        "@type": "Organization",
        "name": offer.shopName
      }
    }))
  };

  return schema;
};

/**
 * OfferCatalog Schema for brand pages
 */
export const generateBrandOffersSchema = (brandData, offers) => {
  if (!brandData || !offers || offers.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "name": `Offres ${brandData.brand}`,
    "description": `Les meilleures offres ${brandData.brand} en promotion`,
    "url": `${SEO_CONFIG.siteUrl}/brand/${encodeURIComponent(brandData.brand)}`,
    "numberOfItems": offers.length,
    "itemListElement": offers.slice(0, 20).map((offer, index) => ({
      "@type": "Offer",
      "position": index + 1,
      "name": fixEncoding(offer.title),
      "price": offer.price,
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "url": offer.goUrl,
      ...(offer.images?.[0]?.url && { "image": offer.images[0].url }),
      ...(offer.merchant?.name && {
        "seller": {
          "@type": "Organization",
          "name": fixEncoding(offer.merchant.name)
        }
      }),
      ...(offer.rebatePercentage > 0 && {
        "priceSpecification": {
          "@type": "PriceSpecification",
          "price": offer.price,
          "priceCurrency": "EUR",
          "valueAddedTaxIncluded": true
        }
      })
    }))
  };
};

/**
 * FAQPage Schema (for potential FAQ sections)
 */
export const generateFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

/**
 * Combine multiple schemas into a single JSON-LD array
 */
export const combineSchemas = (...schemas) => {
  const validSchemas = schemas.filter(s => s !== null && s !== undefined);
  if (validSchemas.length === 0) return null;
  if (validSchemas.length === 1) return validSchemas[0];
  return validSchemas;
};
